/**
 * zehn-aip's doc agent panel in the editor's dock.
 *
 * The dock page (/agent-panel, inside Collabora's frame) frames the panel and
 * must hand it the person's sign-in, but the sign-in arrives at the EDITOR
 * page: the portal's SET_ID_TOKEN (its own IdP) or office's SET_TOKENS. Both
 * pages are this origin under the same top-level site, so the editor page
 * publishes what the panel needs on a BroadcastChannel and the dock page asks
 * for it — memory only, never storage: an ID token signs a person in.
 *
 * What is published:
 *   {mode: 'id_token', tenant, idToken, moduleApis}  — the panel trades the ID
 *     token at aip and at each module itself (moduleApis: docs + VITE_AIP_MODULE_APIS)
 *   {mode: 'tokens', tokens}  — an office bundle that carries a `zehn-aip` token
 *
 * When the editor page has nothing to give — the frame reloaded, and the
 * office does not resend a token it already delivered — or only an office
 * token about to run out, or the panel asks for a fresh one, the page asks
 * the office for new tokens (setAipAuthRefresher); their bundle is published
 * like any other.
 */

import { env } from '@/shared/utils/env';
import { originOf } from '@/shared/utils/collaboraOrigin.js';
import { domain } from '@/shared/utils/request';

const CHANNEL_NAME = 'zehn-docs:aip-auth';
const AIP_MODULE = 'zehn-aip';
const DOCS_MODULE = 'zehn-docs';

// The editor header's Assistant button sits outside the editor frame: it asks
// the open editor (collabora-editor) to show or hide the dock.
export const ASSISTANT_TOGGLE = 'zehn-docs:toggle-assistant';

export function toggleAssistant() {
  window.dispatchEvent(new CustomEvent(ASSISTANT_TOGGLE));
}

/** The panel's URL, or null when this deployment does not use it (the dock then holds only Placeholders). */
export function aipPanelUrl() {
  const url = env('VITE_AIP_PANEL_URL');
  return originOf(url) ? url : null;
}

export function aipModuleApis() {
  let extra = {};
  try {
    extra = JSON.parse(env('VITE_AIP_MODULE_APIS') || '{}') || {};
  } catch {
    extra = {};
  }
  return { [DOCS_MODULE]: domain, ...extra };
}

let channel = null;
let latest = null;
let refresher = null;
const subscribers = new Set();
// An office token with less than this left is renewed rather than handed over.
const MIN_LEFT_MS = 2 * 60 * 1000;

function expiresSoon(auth) {
  if (auth.mode !== 'tokens') return false;
  try {
    const payload = JSON.parse(atob(String(auth.tokens[AIP_MODULE]).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 - Date.now() < MIN_LEFT_MS;
  } catch {
    return false;
  }
}

function getChannel() {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event) => {
    const data = event.data || {};
    if (data.type === 'request') {
      if (latest && !(refresher && (data.fresh || expiresSoon(latest)))) channel.postMessage({ type: 'auth', auth: latest });
      else refresher?.();
    }
    if (data.type === 'auth' && data.auth) subscribers.forEach((fn) => fn(data.auth));
  };
  return channel;
}

/** Editor page: how to get new tokens from the office when the panel needs them. */
export function setAipAuthRefresher(fn) {
  if (!aipPanelUrl()) return;
  refresher = fn;
  getChannel();
}

/** Editor page: what the panel signs in with now (called on every sign-in and renewal). */
export function publishAipAuth(auth) {
  if (!aipPanelUrl() || !auth) return;
  latest = auth;
  getChannel()?.postMessage({ type: 'auth', auth });
}

/** The portal's ID token, for the panel to trade at aip and at the modules. */
export function publishIdToken(tenant, idToken) {
  publishAipAuth({ mode: 'id_token', tenant, idToken, moduleApis: aipModuleApis() });
}

/** An office bundle, when it carries a token for zehn-aip. */
export function publishTokenBundle(tokens) {
  if (tokens && tokens[AIP_MODULE]) publishAipAuth({ mode: 'tokens', tokens });
}

/** Dock page: every sign-in the editor page publishes from now on. */
export function subscribeAipAuth(fn) {
  getChannel();
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** Dock page: the current sign-in, or null when the editor page has none to give.
 * `fresh`: not the one it holds — the panel's has run out. */
export function requestAipAuth({ fresh = false, timeoutMs = 3000 } = {}) {
  return new Promise((resolve) => {
    let timer = null;
    const off = subscribeAipAuth((auth) => {
      clearTimeout(timer);
      off();
      resolve(auth);
    });
    timer = setTimeout(() => {
      off();
      resolve(null);
    }, timeoutMs);
    getChannel()?.postMessage({ type: 'request', fresh });
  });
}
